import { SCRIPT_LOAD_TIMEOUT } from '../constants/common';
import { AMEX, DISCOVER, MA, MASTERCARD, VISA } from '../constants/partnering-networks';
import isValidUrl from './is-valid-url';
import logger from './logger';
import OrchCustomData from './orch-custom-data';

// utility sdk functions to validate the cardBrand
export const isAmexSdk = cardBrand => cardBrand && cardBrand.toUpperCase() === AMEX;
export const isDiscoverSdk = cardBrand => cardBrand && cardBrand.toUpperCase() === DISCOVER;
export const isMaSdk = cardBrand => cardBrand && cardBrand.toUpperCase() === MASTERCARD;
export const isVisaSdk = cardBrand => cardBrand && cardBrand.toUpperCase() === VISA;
const isUrl = url => url && (/localhost/.test(url) || isValidUrl(url));

/**
 * Dynamic Injection of SDK script. Avoid duplicates based on the script id
 *  - cardBrand is used to distinguish unique sdk script configured in DCP portal
 *  - Per understanding, DCP Portal can configure only one sdk script per participating
 *    INO network
 * @param {string} url sdkUrl
 * @param {string} sdkCardBrand
 * @param {string} sdkApiKey
 * @return {Promise} init response promise, always resolve
 *
 */
export const loadScript = function(url, sdkCardBrand, sdkApiKey) {
  return new Promise(resolve => {
    let settled = false;
    let network = '';
    const isVisa = isVisaSdk(sdkCardBrand);

    network = isMaSdk(sdkCardBrand) ? MA.toLowerCase() : sdkCardBrand.toLowerCase();

    let timeoutId = null;

    // VCO-14858 - Visa SDK MUST be loaded for the app to work. If the Visa SDK
    // fails to load then there is no point launching the app because at some
    // point it will inevitably fail (e.g. when trying to load the DCF). If the
    // Visa SDK takes too long to load, then we should make it load faster
    // rather than use a hack to load the lightbox before the SDK resolves,
    // which opens the door to all sorts of unknown bugs.
    if (!isVisa) {
      // eslint-disable-next-line no-use-before-define
      timeoutId = setTimeout(() => rejectPromise(), SCRIPT_LOAD_TIMEOUT);
    }

    const resolvePromise = () => {
      if (!settled) {
        settled = true;
        clearTimeout(timeoutId);
        OrchCustomData.setNetwork(network.toUpperCase());
        resolve({ apiKey: sdkApiKey, isError: false, network: sdkCardBrand });
      }
    };

    const rejectPromise = () => {
      if (!settled) {
        settled = true;
        clearTimeout(timeoutId);
        resolve({ apiKey: sdkApiKey, isError: true, network: sdkCardBrand });
      }
    };

    if (!isUrl(url)) {
      rejectPromise();
      return;
    }

    const id = `${network}SDK`;
    const existingScript = document.getElementById(id);

    if (existingScript) {
      logger(`script already loaded =`, url);
      rejectPromise();
      return;
    }

    const scriptElement = document.createElement('script');

    // For testing purpose only.
    if (window.location.href.includes('localhost') && url.endsWith('AmexProxySdk.js')) {
      url = 'https://qicm.americanexpress.com/Internet/IMDC/src/js/bundle.js';
    }

    // URL for the third-party library being loaded.
    scriptElement.src = url;
    scriptElement.id = id;
    scriptElement.onload = () => resolvePromise();
    scriptElement.onerror = () => rejectPromise();

    document.body.appendChild(scriptElement);
  });
};

export default loadScript;
